import clsx from "clsx"
import { useEffect, useRef } from "react"
import { isDesktop } from "react-device-detect"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"
import { PropsWithClassName } from "utils/types"

import TutorialThumbnailTablet from "../assets/tutorial-thumbnail-tablet.webp"
import TutorialThumbnailWeb from "../assets/tutorial-thumbnail-web.webp"

// Percentage-of-duration milestones at which a progress event is fired.
const WATCH_MILESTONES = [5, 15, 25, 50, 75, 90, 95, 100]

const TutorialVideo = (
  props: PropsWithClassName<{
    videoUrl?: string | null
    courseId?: number
    courseSlug?: string
    courseName?: string
  }>
) => {
  const startedRef = useRef(false)
  const completedRef = useRef(false)
  const milestonesRef = useRef<Set<number>>(new Set())
  const watchtimeRef = useRef(0)
  const lastTimeRef = useRef(0)

  // This component isn't remounted when navigating between courses (no
  // `key` on the route), so reset all tracking state whenever the
  // underlying video changes so watch progress from a previously viewed
  // course's video doesn't leak into the next one.
  const videoIdentity = `${props.courseId ?? ""}:${props.videoUrl ?? ""}`
  useEffect(() => {
    startedRef.current = false
    completedRef.current = false
    milestonesRef.current = new Set()
    watchtimeRef.current = 0
    lastTimeRef.current = 0
  }, [videoIdentity])

  const eventData = (extra?: object) => ({
    course_id: props.courseId,
    course_slug: props.courseSlug,
    course_name: props.courseName,
    video_src: props.videoUrl,
    ...extra,
  })

  const track = (eventName: string, extra?: object) => {
    trackingService.trackEvent(eventName, eventData(extra), {
      services: ["PostHog"],
    })
  }

  const handlePlay = () => {
    if (!startedRef.current) {
      startedRef.current = true
      track(TrackingEvents.assignmentTutorialVideoStarted)
    }
  }

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget
    if (!video.duration) return

    // Accumulate real watchtime, ignoring forward seeks (delta > 2s).
    const delta = video.currentTime - lastTimeRef.current
    if (delta > 0 && delta < 2) watchtimeRef.current += delta
    lastTimeRef.current = video.currentTime

    const pct = (video.currentTime / video.duration) * 100
    WATCH_MILESTONES.forEach(milestone => {
      if (pct >= milestone && !milestonesRef.current.has(milestone)) {
        milestonesRef.current.add(milestone)
        track(TrackingEvents.assignmentTutorialVideoProgress, {
          milestone,
          watchtime_seconds: Math.round(watchtimeRef.current),
        })
      }
    })
  }

  const handleEnded = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (completedRef.current) return
    completedRef.current = true
    track(TrackingEvents.assignmentTutorialVideoCompleted, {
      watchtime_seconds: Math.round(watchtimeRef.current),
      duration_seconds: Math.round(event.currentTarget.duration || 0),
    })
  }

  if (!props.videoUrl) return null

  return (
    /* eslint-disable-next-line jsx-a11y/media-has-caption */
    <video
      controlsList="nodownload"
      controls
      className={clsx(
        "w-full rounded-xl shadow-[0px_6px_15px_0px_rgba(0,0,0,0.05)]",
        props.className
      )}
      preload="none"
      poster={isDesktop ? TutorialThumbnailWeb : TutorialThumbnailTablet}
      src={props.videoUrl}
      onPlay={handlePlay}
      onTimeUpdate={handleTimeUpdate}
      onEnded={handleEnded}
    />
  )
}

export default TutorialVideo
